import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Megaphone, Upload, Send, Loader2, Pause, Play, X,
  Users, Clock, Paperclip, Eye, AlertTriangle, Trash2, ShieldCheck
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import api from '../services/api';

// Durum etiketleri (TR) + renk
const STATUS_META = {
  queued: { label: 'Kuyrukta', variant: 'secondary' },
  sending: { label: 'Gönderiliyor', variant: 'default' },
  paused: { label: 'Duraklatıldı', variant: 'outline' },
  completed: { label: 'Tamamlandı', variant: 'default' },
  cancelled: { label: 'İptal edildi', variant: 'destructive' },
  failed: { label: 'Hata', variant: 'destructive' },
};

// Dosyayı base64'e çevir (data: ön ekini at)
const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result || '';
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

// Geçerli alıcı satırlarını say
const countValidRecipients = (text) => {
  const re = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
  const seen = new Set();
  let n = 0;
  (text || '').split('\n').forEach((raw) => {
    const line = raw.trim();
    if (!line) return;
    const email = (line.includes(',') ? line.split(',')[0] : line).trim().toLowerCase();
    if (re.test(email) && !seen.has(email)) {
      seen.add(email);
      n += 1;
    }
  });
  return n;
};

export const BulkMailPage = () => {
  const [config, setConfig] = useState(null);
  const [activeTab, setActiveTab] = useState('compose');

  // Composer state
  const [subject, setSubject] = useState('');
  const [fromName, setFromName] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [recipientsText, setRecipientsText] = useState('');
  const [rateCount, setRateCount] = useState(3);
  const [rateUnit, setRateUnit] = useState('minute');
  const [attachments, setAttachments] = useState([]); // {filename, content, content_type}
  const [showPreview, setShowPreview] = useState(false);
  const [testTo, setTestTo] = useState('');

  const [isSending, setIsSending] = useState(false);
  const [isTesting, setIsTesting] = useState(false);

  // MX doğrulama
  const [checkMx, setCheckMx] = useState(true);
  const [isValidating, setIsValidating] = useState(false);
  const [validation, setValidation] = useState(null); // {valid_count, invalid_count, invalid:[{email,domain}]}

  // Campaigns
  const [campaigns, setCampaigns] = useState([]);
  const htmlFileRef = useRef(null);
  const attachRef = useRef(null);

  const validCount = countValidRecipients(recipientsText);
  const hourlyEquivalent = rateUnit === 'minute' ? rateCount * 60 : rateCount;
  const overLimit = config && config.hourly_limit && hourlyEquivalent > config.hourly_limit;

  // Tahmini süre
  const delaySec = (rateUnit === 'minute' ? 60 : 3600) / Math.max(1, rateCount);
  const estSeconds = Math.round(validCount * delaySec);
  const estLabel =
    estSeconds < 60 ? `${estSeconds} sn`
    : estSeconds < 3600 ? `${Math.round(estSeconds / 60)} dk`
    : `${(estSeconds / 3600).toFixed(1)} saat`;

  // Config yükle
  useEffect(() => {
    api.getBulkConfig()
      .then((cfg) => {
        setConfig(cfg);
        setTestTo(cfg.email || '');
      })
      .catch(() => toast.error('Toplu mail ayarları yüklenemedi'));
  }, []);

  // Kampanyaları yükle + canlı ilerleme için polling
  const loadCampaigns = useCallback(async () => {
    try {
      const res = await api.getBulkCampaigns();
      setCampaigns(res.campaigns || []);
    } catch (e) {
      // sessiz
    }
  }, []);

  useEffect(() => {
    loadCampaigns();
    const interval = setInterval(loadCampaigns, 4000);
    return () => clearInterval(interval);
  }, [loadCampaigns]);

  // HTML dosyası içe aktar
  const handleHtmlFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    setBodyHtml(text);
    toast.success(`${file.name} içe aktarıldı`);
    e.target.value = '';
  };

  // Ek dosya ekle
  const handleAttachFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    const added = [];
    for (const file of files) {
      const content = await fileToBase64(file);
      added.push({ filename: file.name, content, content_type: file.type || 'application/octet-stream' });
    }
    setAttachments((prev) => [...prev, ...added]);
    e.target.value = '';
  };

  const removeAttachment = (idx) => {
    setAttachments((prev) => prev.filter((_, i) => i !== idx));
  };

  const buildPayload = () => ({
    subject,
    from_name: fromName || null,
    body_html: bodyHtml,
    attachments: attachments.length ? attachments : null,
  });

  // Test gönder
  const handleTest = async () => {
    if (!subject.trim() || !bodyHtml.trim()) {
      toast.error('Konu ve içerik gerekli');
      return;
    }
    setIsTesting(true);
    try {
      const res = await api.sendBulkTest({ ...buildPayload(), to: testTo || null });
      toast.success(res.message || 'Test maili gönderildi');
    } catch (e) {
      toast.error(e.message || 'Test maili gönderilemedi');
    } finally {
      setIsTesting(false);
    }
  };

  // Alıcı listesini MX'e göre doğrula
  const handleValidate = async () => {
    const lines = recipientsText.split('\n').map((l) => l.trim()).filter(Boolean);
    if (lines.length === 0) return toast.error('Önce alıcı girin');
    setIsValidating(true);
    try {
      const res = await api.validateBulkRecipients(lines);
      setValidation(res);
      if (res.invalid_count === 0) {
        toast.success(`Tüm domainler geçerli (${res.valid_count} alıcı)`);
      } else {
        toast.warning(`${res.invalid_count} geçersiz domain bulundu`);
      }
    } catch (e) {
      toast.error(e.message || 'Doğrulama başarısız');
    } finally {
      setIsValidating(false);
    }
  };

  // Geçersiz alıcıları textarea'dan çıkar
  const removeInvalid = () => {
    if (!validation?.invalid?.length) return;
    const invalidSet = new Set(validation.invalid.map((i) => i.email.toLowerCase()));
    const kept = recipientsText.split('\n').filter((line) => {
      const t = line.trim();
      if (!t) return false;
      const email = (t.includes(',') ? t.split(',')[0] : t).trim().toLowerCase();
      return !invalidSet.has(email);
    });
    setRecipientsText(kept.join('\n'));
    setValidation(null);
    toast.success('Geçersiz alıcılar kaldırıldı');
  };

  // Kampanyayı kuyruğa al
  const handleSubmit = async () => {
    if (!subject.trim()) return toast.error('Konu boş olamaz');
    if (!bodyHtml.trim()) return toast.error('Mail içeriği boş olamaz');
    if (validCount === 0) return toast.error('Geçerli en az bir alıcı gerekli');
    if (overLimit) return toast.error(`Hız saatlik tavanı (${config.hourly_limit}/saat) aşıyor`);

    setIsSending(true);
    try {
      const recipients = recipientsText.split('\n').map((l) => l.trim()).filter(Boolean);
      const res = await api.createBulkCampaign({
        ...buildPayload(),
        recipients,
        rate_count: Number(rateCount),
        rate_unit: rateUnit,
        check_mx: checkMx,
      });
      toast.success(res.message || 'Kampanya kuyruğa alındı');
      // Formu sıfırla
      setSubject(''); setFromName(''); setBodyHtml('');
      setRecipientsText(''); setAttachments([]); setValidation(null);
      setActiveTab('campaigns');
      loadCampaigns();
    } catch (e) {
      toast.error(e.message || 'Kampanya oluşturulamadı');
    } finally {
      setIsSending(false);
    }
  };

  const doAction = async (id, action) => {
    try {
      if (action === 'pause') await api.pauseBulkCampaign(id);
      if (action === 'resume') await api.resumeBulkCampaign(id);
      if (action === 'cancel') await api.cancelBulkCampaign(id);
      loadCampaigns();
    } catch (e) {
      toast.error(e.message || 'İşlem başarısız');
    }
  };

  return (
    <div className="flex-1 overflow-hidden flex flex-col bg-background" data-testid="bulk-page">
      {/* Header */}
      <div className="flex items-center gap-3 px-6 py-4 border-b border-border/50">
        <Megaphone className="h-5 w-5 text-primary" />
        <div>
          <h1 className="text-lg font-semibold">Toplu Mail</h1>
          {config && (
            <p className="text-xs text-muted-foreground">
              Limit: {config.hourly_limit}/saat · {config.daily_limit}/gün
            </p>
          )}
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
        <div className="px-6 pt-4">
          <TabsList>
            <TabsTrigger value="compose" data-testid="tab-compose">Yeni Kampanya</TabsTrigger>
            <TabsTrigger value="campaigns" data-testid="tab-campaigns">
              Kampanyalarım{campaigns.length ? ` (${campaigns.length})` : ''}
            </TabsTrigger>
          </TabsList>
        </div>

        {/* ====== Composer ====== */}
        <TabsContent value="compose" className="flex-1 overflow-hidden mt-0">
          <ScrollArea className="h-full">
            <div className="max-w-3xl mx-auto p-6 space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Konu</Label>
                  <Input value={subject} onChange={(e) => setSubject(e.target.value)}
                    placeholder="Mail konusu" data-testid="bulk-subject" />
                </div>
                <div className="space-y-2">
                  <Label>Gönderen Adı (opsiyonel)</Label>
                  <Input value={fromName} onChange={(e) => setFromName(e.target.value)}
                    placeholder="Örn. Netlen İnternet" />
                </div>
              </div>

              {/* HTML içerik */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>HTML İçerik</Label>
                  <div className="flex gap-2">
                    <input ref={htmlFileRef} type="file" accept=".html,.htm,text/html"
                      className="hidden" onChange={handleHtmlFile} />
                    <Button type="button" variant="outline" size="sm" onClick={() => htmlFileRef.current?.click()}>
                      <Upload className="h-4 w-4 mr-1" /> HTML içe aktar
                    </Button>
                    <Button type="button" variant="outline" size="sm" onClick={() => setShowPreview((v) => !v)}
                      disabled={!bodyHtml.trim()}>
                      <Eye className="h-4 w-4 mr-1" /> {showPreview ? 'Önizlemeyi kapat' : 'Önizle'}
                    </Button>
                  </div>
                </div>
                <Textarea value={bodyHtml} onChange={(e) => setBodyHtml(e.target.value)}
                  placeholder="HTML tasarımını buraya yapıştırın veya dosyadan içe aktarın. {{email}} ve {{ad}} alanları her alıcıya göre doldurulur."
                  className="font-mono text-xs min-h-[180px]" data-testid="bulk-body" />
                <p className="text-xs text-muted-foreground">
                  Kişiselleştirme: <code>{'{{email}}'}</code>, <code>{'{{ad}}'}</code> kullanılabilir.
                </p>
                {showPreview && bodyHtml.trim() && (
                  <div className="border border-border rounded-lg overflow-hidden">
                    <iframe title="preview" sandbox="" srcDoc={bodyHtml} className="w-full h-80 bg-white" />
                  </div>
                )}
              </div>

              {/* Alıcılar */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="flex items-center gap-1"><Users className="h-4 w-4" /> Alıcılar</Label>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-muted-foreground">{validCount} geçerli alıcı</span>
                    <Button type="button" variant="outline" size="sm" onClick={handleValidate}
                      disabled={isValidating} data-testid="bulk-validate">
                      {isValidating ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <ShieldCheck className="h-4 w-4 mr-1" />}
                      Listeyi Doğrula (MX)
                    </Button>
                  </div>
                </div>
                <Textarea value={recipientsText}
                  onChange={(e) => { setRecipientsText(e.target.value); if (validation) setValidation(null); }}
                  placeholder={"Her satıra bir adres:\nahmet@ornek.com\nmehmet@ornek.com,Mehmet Yılmaz"}
                  className="font-mono text-xs min-h-[140px]" data-testid="bulk-recipients" />
                <p className="text-xs text-muted-foreground">
                  Format: <code>email</code> veya <code>email,Ad Soyad</code> (isim {'{{ad}}'} için kullanılır).
                </p>

                {/* MX doğrulama sonucu */}
                {validation && validation.invalid_count > 0 && (
                  <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-destructive flex items-center gap-1">
                        <AlertTriangle className="h-4 w-4" />
                        {validation.invalid_count} geçersiz domain (MX kaydı yok)
                      </span>
                      <Button type="button" variant="destructive" size="sm" onClick={removeInvalid}>
                        <Trash2 className="h-4 w-4 mr-1" /> Geçersizleri Kaldır
                      </Button>
                    </div>
                    <div className="max-h-28 overflow-y-auto text-xs font-mono text-muted-foreground space-y-0.5">
                      {validation.invalid.map((i, idx) => (
                        <div key={idx} className="truncate">{i.email}</div>
                      ))}
                    </div>
                  </div>
                )}

                {/* MX kontrol seçeneği */}
                <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
                  <input type="checkbox" checked={checkMx} onChange={(e) => setCheckMx(e.target.checked)}
                    className="rounded border-border" />
                  Kuyruğa alırken geçersiz domainleri (MX'siz) otomatik ele
                </label>
              </div>

              {/* Hız */}
              <div className="space-y-2">
                <Label className="flex items-center gap-1"><Clock className="h-4 w-4" /> Gönderim Hızı</Label>
                <div className="flex items-center gap-2">
                  <Input type="number" min={1} value={rateCount}
                    onChange={(e) => setRateCount(Math.max(1, Number(e.target.value) || 1))}
                    className="w-24" data-testid="bulk-rate-count" />
                  <span className="text-sm">adet /</span>
                  <Select value={rateUnit} onValueChange={setRateUnit}>
                    <SelectTrigger className="w-32" data-testid="bulk-rate-unit"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="minute">dakika</SelectItem>
                      <SelectItem value="hour">saat</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {overLimit ? (
                  <p className="text-xs text-destructive flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" /> Bu hız ({hourlyEquivalent}/saat) tavanı aşıyor (max {config.hourly_limit}/saat).
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    ≈ {hourlyEquivalent}/saat · {validCount} alıcı tahmini ~{estLabel}'de gönderilir.
                  </p>
                )}
              </div>

              {/* Ekler */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="flex items-center gap-1"><Paperclip className="h-4 w-4" /> Ekler (opsiyonel)</Label>
                  <input ref={attachRef} type="file" multiple className="hidden" onChange={handleAttachFiles} />
                  <Button type="button" variant="outline" size="sm" onClick={() => attachRef.current?.click()}>
                    <Paperclip className="h-4 w-4 mr-1" /> Dosya ekle
                  </Button>
                </div>
                {attachments.length > 0 && (
                  <div className="space-y-1">
                    {attachments.map((a, i) => (
                      <div key={i} className="flex items-center justify-between text-xs bg-muted rounded px-2 py-1">
                        <span className="truncate">{a.filename}</span>
                        <button onClick={() => removeAttachment(i)} className="text-muted-foreground hover:text-destructive">
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                    <p className="text-xs text-muted-foreground">Ekler toplam 5 MB ile sınırlıdır.</p>
                  </div>
                )}
              </div>

              {/* Test + Gönder */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-end gap-3 pt-2 border-t border-border/50">
                <div className="flex-1 space-y-1">
                  <Label className="text-xs">Test adresi</Label>
                  <Input value={testTo} onChange={(e) => setTestTo(e.target.value)}
                    placeholder="kendi@adresin.com" className="h-9" />
                </div>
                <Button type="button" variant="outline" onClick={handleTest} disabled={isTesting} data-testid="bulk-test">
                  {isTesting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}
                  Test Gönder
                </Button>
                <Button type="button" onClick={handleSubmit} disabled={isSending || overLimit} data-testid="bulk-submit">
                  {isSending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Megaphone className="h-4 w-4 mr-1" />}
                  Kuyruğa Al ({validCount})
                </Button>
              </div>
            </div>
          </ScrollArea>
        </TabsContent>

        {/* ====== Campaigns ====== */}
        <TabsContent value="campaigns" className="flex-1 overflow-hidden mt-0">
          <ScrollArea className="h-full">
            <div className="max-w-3xl mx-auto p-6 space-y-3">
              {campaigns.length === 0 && (
                <div className="text-center text-muted-foreground py-16">
                  <Megaphone className="h-10 w-10 mx-auto mb-3 opacity-40" />
                  Henüz kampanya yok.
                </div>
              )}
              {campaigns.map((c) => {
                const meta = STATUS_META[c.status] || { label: c.status, variant: 'secondary' };
                const done = (c.sent_count || 0) + (c.failed_count || 0);
                const pct = c.total_recipients ? Math.round((done / c.total_recipients) * 100) : 0;
                return (
                  <div key={c.id} className="border border-border rounded-lg p-4 space-y-3" data-testid="campaign-card">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-medium truncate">{c.subject}</div>
                        <div className="text-xs text-muted-foreground">
                          {c.rate_count}/{c.rate_unit === 'minute' ? 'dk' : 'saat'} · {c.total_recipients} alıcı
                        </div>
                      </div>
                      <Badge variant={meta.variant}>{meta.label}</Badge>
                    </div>

                    <Progress value={pct} className="h-2" />
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span>
                        Gönderildi: {c.sent_count || 0}
                        {c.failed_count ? ` · Başarısız: ${c.failed_count}` : ''} / {c.total_recipients}
                      </span>
                      <span>%{pct}</span>
                    </div>

                    {/* Aksiyonlar */}
                    <div className="flex gap-2">
                      {(c.status === 'queued' || c.status === 'sending') && (
                        <Button size="sm" variant="outline" onClick={() => doAction(c.id, 'pause')}>
                          <Pause className="h-3 w-3 mr-1" /> Duraklat
                        </Button>
                      )}
                      {c.status === 'paused' && (
                        <Button size="sm" variant="outline" onClick={() => doAction(c.id, 'resume')}>
                          <Play className="h-3 w-3 mr-1" /> Devam Et
                        </Button>
                      )}
                      {(c.status === 'queued' || c.status === 'sending' || c.status === 'paused') && (
                        <Button size="sm" variant="ghost" className="text-destructive"
                          onClick={() => doAction(c.id, 'cancel')}>
                          <Trash2 className="h-3 w-3 mr-1" /> İptal
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </ScrollArea>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default BulkMailPage;
